<script lang="ts" setup>
    const slug = currentSlug()
    const { data: page, refresh } =
        await useStatamicEntries().getEntryBySlug<Page>({
            collection: 'pages',
            value: slug,
        })

    useStatamicPreview(refresh)

    const { data: products } = await useStatamicEntries().getEntries<Product>({
        collection: 'products',
    })

    useHead({
        bodyAttrs: {
            class: {
                [`page-${slug}`]: true,
                'with-feature-image': computed(
                    () => page.value?.feature_image != null,
                ),
            },
        },
        title: () => page.value?.title ?? 'Our Shop',
    })

    const activeVariant = ref<string>()
</script>

<template>
    <div class="page block-gap-b" :class="`page-${slug}`">
        <PageHeader
            :image="page.feature_image"
            :content="page.page_intro ?? ''"
            :faded="true"
        >
            <h1>{{ page.title }}</h1>
        </PageHeader>

        <RoomToText
            v-if="page.room_to_content"
            :content="page.room_to_content"
            :toOffset="page.room_to_to_offset"
        />

        <Blocks v-if="page.blocks" :blocks="page.blocks" />

        <ul class="products layout-grid block-gap-t gap-y-12 md:gap-y-24">
            <li
                v-for="product in products"
                :key="product.id"
                class="col-span-6 md:col-span-7 md:odd:col-start-2 md:even:col-start-9 lg:col-span-6 lg:even:col-start-10"
            >
                <NuxtLink :to="product.url">
                    <div class="relative bg-white">
                        <Asset
                            v-if="product.feature_image"
                            :asset="product.feature_image"
                            size="large"
                            class="aspect-[713/558] h-auto max-h-full w-full"
                        />

                        <ul
                            v-if="product.product_variants"
                            v-for="variant in product.product_variants.variants"
                            class="absolute inset-0"
                        >
                            <li
                                v-for="option in product.product_variants
                                    .options"
                                class="absolute inset-0 opacity-0 transition"
                                :class="{
                                    'opacity-100':
                                        activeVariant === option.variant,
                                }"
                            >
                                <img
                                    v-if="option.product_image?.url"
                                    :src="option.product_image.url"
                                    :alt="`${option.variant} ${variant.name}`"
                                    class="aspect-[713/558] h-auto max-h-full w-full"
                                />
                            </li>
                        </ul>
                    </div>

                    <h3 class="mt-2 uppercase">{{ product.title }}</h3>

                    <ProductPrice :product="product" />

                    <ul
                        v-if="product.product_variants"
                        class="mt-2 border-t pt-2"
                    >
                        <li
                            v-for="variant in product.product_variants.variants"
                        >
                            <ProductVariantSwatches
                                :name="variant.name"
                                :values="variant.values"
                                :options="product.product_variants.options"
                                v-model="activeVariant"
                            />
                        </li>
                    </ul>
                </NuxtLink>
            </li>
        </ul>
    </div>
</template>
